import React, { useState } from 'react';
import "../style.scss";
import { THeadRow, RowCells } from './components/DueListTable.jsx';
import { DuelistFilter } from "./components/DueListFilter";
import { DueListPagination } from "./components/DueListPagination";
import LoaderBox from '../components/custom/loader/loaderBox';
import SkeletonTable from './components/SkeletonTable';
import ListLoadError from './components/ListLoadError';
import ProcessModal from './components/ProcessModal';
import {
    Alert,
    Container,
    Row,
    Col,
    Card,
    CardHeader,
    CardBody
} from 'reactstrap';
import { TableVirtuoso } from 'react-virtuoso';
import { useProcessList } from './hooks/useProcessList';

const useTableHeight = () => {
    const [height, setHeight] = React.useState(600);
    React.useEffect(() => {
        const calc = () => {
            const w = window.innerWidth;
            setHeight(w < 768 ? 450 : w < 992 ? 500 : 600);
        };
        calc();
        window.addEventListener('resize', calc);
        return () => window.removeEventListener('resize', calc);
    }, []);
    return height;
};

const Duelist = () => {
    const {
        data: logMapData,
        ordersStatus: logOrderStatus,
        users,
        loader,
        loadError,
        toast,
        progress,
        exportMessage,
        exportCancellable,
        reload: executeAPIFunctions,
        exportExcel: getExportedExcel,
        cancelExport,
        showToast,
    } = useProcessList();

    const tableHeight = useTableHeight();
    const [openModalOrderId, setOpenModalOrderId] = useState(null);

    // Deriva o objeto order a partir do id aberto — o modal não desmonta ao recarregar
    const openOrder = logMapData.results.find((r) => r.id === openModalOrderId) ?? null;

    return (
        <>
            {toast && (
                <Alert
                    role="alert"
                    color={toast.type === 'success' ? 'success' : toast.type === 'warning' ? 'warning' : 'danger'}
                    style={{ position: 'fixed', top: '16px', right: '16px', zIndex: 10000, minWidth: '280px' }}
                >
                    {toast.text}
                </Alert>
            )}

            {/* Modal renderizado fora do TableVirtuoso — não desmonta ao recarregar dados */}
            {openOrder && (
                <ProcessModal
                    order={openOrder}
                    onClose={() => setOpenModalOrderId(null)}
                    reload={executeAPIFunctions}
                    endpoint={logMapData.nowEndpoint}
                    ordersStatus={logOrderStatus}
                    users={users}
                    showToast={showToast}
                />
            )}

            <div className="content">
                <Container fluid>
                    <Row className="mb-4 align-items-center">
                        <Col>
                            <h2 className="mb-0 text-dark" style={{ fontWeight: "700" }}>
                                <i className="ni ni-tv-2 text-primary mr-2" />
                                Lista Geral de Precatórios
                            </h2>
                            <p className="text-muted mb-0 small">Consulte, filtre e gerencie todos os processos cadastrados no sistema.</p>
                        </Col>
                    </Row>

                    <Row>
                        <Col>
                            <Card className="shadow-sm border-0 mb-4">
                                <CardHeader className="bg-transparent pb-0">
                                    <h4 className="mb-0 text-muted font-weight-600">Configuração de Busca</h4>
                                </CardHeader>
                                <CardBody>
                                    <DuelistFilter
                                        showToast={showToast}
                                        reload={executeAPIFunctions}
                                        endpoint={logMapData.nowEndpoint}
                                        orderStatus={logOrderStatus}
                                        executeExport={getExportedExcel}
                                        recordCount={logMapData.count}
                                    />
                                </CardBody>
                            </Card>

                            <Card className="shadow-sm border-0">
                                <CardHeader className="bg-transparent pb-0 d-flex justify-content-between align-items-center">
                                    <h4 className="mb-0 text-muted font-weight-600">
                                        Resultados Encontrados
                                        {logMapData.count > 0 && (
                                            <span className="ml-2 badge badge-pill badge-primary">{logMapData.count}</span>
                                        )}
                                    </h4>
                                </CardHeader>
                                <CardBody className="p-0">
                                    {loader ? (
                                        exportMessage ? (
                                            <div className="py-5">
                                                <LoaderBox
                                                    message={exportMessage}
                                                    progress={progress}
                                                    onCancel={exportCancellable ? cancelExport : undefined}
                                                />
                                            </div>
                                        ) : (
                                            <SkeletonTable />
                                        )
                                    ) : (
                                        <>
                                            <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                                            <TableVirtuoso
                                                data={logMapData.results || []}
                                                style={{ height: tableHeight }}
                                                components={{
                                                    Table: (props) => (
                                                        <table id="custom-duelist-table" className="align-items-center table table-flush" {...props} />
                                                    ),
                                                    TableHead: React.forwardRef((props, ref) => (
                                                        <thead className="thead-light" ref={ref} {...props} />
                                                    )),
                                                    TableRow: (props) => <tr className="custom-duelist-row" {...props} />,
                                                    EmptyPlaceholder: () => loadError ? (
                                                        <ListLoadError
                                                            message={loadError}
                                                            onRetry={() => executeAPIFunctions(logMapData.nowEndpoint || undefined)}
                                                        />
                                                    ) : (
                                                        <tbody>
                                                            <tr>
                                                                <td colSpan={9} className="text-center py-5">
                                                                    <i className="ni ni-folder-17 display-4 text-muted mb-3" />
                                                                    <p className="text-muted">Nenhum precatório encontrado para os filtros selecionados.</p>
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    ),
                                                }}
                                                fixedHeaderContent={() => <THeadRow />}
                                                itemContent={(index, order) => (
                                                    <RowCells
                                                        showToast={showToast}
                                                        order={order}
                                                        i={index}
                                                        reload={executeAPIFunctions}
                                                        endpoint={logMapData.nowEndpoint}
                                                        ordersStatus={logOrderStatus}
                                                        users={users}
                                                        isModalOpen={false}
                                                        onOpenModal={() => setOpenModalOrderId(order.id)}
                                                        onCloseModal={() => setOpenModalOrderId(null)}
                                                    />
                                                )}
                                            />
                                            </div>
                                            <div className="py-3 px-4 bg-transparent">
                                                <DueListPagination
                                                    data={[logMapData.count, logMapData.previous, logMapData.next, logMapData.now, logMapData.nowEndpoint]}
                                                    reload={executeAPIFunctions}
                                                />
                                            </div>
                                        </>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </>
    );
};

export default Duelist;
